import moment from "moment";
import { cleanPhoneNumber } from "utils";
import { defaultLanguage, languageValueToId } from "utils/language";

const DEFAULT_TIMEZONE = moment.tz.guess();
const DEFAULT_COUNTRY = 'United States';

const streetFields = ['address 1', 'address 2', 'patient primary address'];
const cityFields = ['city', 'patient primary city'];
const stateFields = ['state', 'patient primary state'];
const zipFields = ['zip code', 'zip', 'patient primary zip code'];
const countryFields = ['country', 'patient primary country'];
const addressFields = [...streetFields, ...cityFields, ...stateFields, ...zipFields, ...countryFields];
export const getAddressFromFields = (formatFields, fields) => {
  const line1 = fields.find(z => z && (streetFields.includes(z.field) && z.field !== 'address 2'));
  const line2 = fields.find(z => z && (streetFields.includes(z.field) && z.field === 'address 2'));
  const city = fields.find(z => z && (cityFields.includes(z.field)));
  const state = fields.find(z => z && (stateFields.includes(z.field)));
  const zip = fields.find(z => z && (zipFields.includes(z.field)));
  const country = fields.find(z => z && (countryFields.includes(z.field)));

  const zipValue = typeof zip?.value === 'number' ? zip.value.toString() : zip?.value;
  return {street: [line1?.value, line2?.value].filter(v => v).join(', '), city: city?.value, state: state?.value, zip: zipValue, country: country?.value || DEFAULT_COUNTRY};
};

export const trimOrUndefined = v => {
  if (!v) return undefined;
  if (typeof v !== 'string') return v;
  return v.trim();
};

const readDate = date => {
  if (!date) return undefined;
  if (typeof date === 'string') {
    let m = moment(date).tz(DEFAULT_TIMEZONE);
    if (m.isValid()) {
      return m;
    }
    m = moment(date, "DD.MM.YYYY").tz(DEFAULT_TIMEZONE);
    if (m.isValid()) {
      return m;
    }
    return date;
  }
  const d = new Date(date);
  return moment(d);
};

const calcPhoneNumber = phone => {
  if (!phone) return null;
  const val = trimOrUndefined(phone.toString());
  const cleanPhone = cleanPhoneNumber(val);
  if (val.startsWith('+')) return `+${cleanPhone}`;
  if (cleanPhone.length === 10) return `+1${cleanPhone}`;
  return cleanPhone;
};

const getGender = gender => {
  switch (gender) {
    case 'M':
    case 'Male':
      return 'Male';
    case 'F':
    case 'Female':
      return 'Female';
    default:
      return trimOrUndefined(gender);
  }
};

export const fieldTypes = {
  simpleField: 'simpleField',
  combinedField: 'combinedField',
  simpleArray: 'simpleArray',
  complexArray: 'complexArray',
};

export const fieldList = conditions => ({
  firstName: {
    fieldType: fieldTypes.simpleField,
    columnNames: ['firstname', 'first name', 'primary name', 'patient first name'],
    required: true,
    transform: val => trimOrUndefined(val),
  },
  lastName: {
    fieldType: fieldTypes.simpleField,
    columnNames: ['lastname', 'last name', 'patient last name'],
    required: true,
    transform: val => trimOrUndefined(val),
  },
  pharmacyPatientId: {
    fieldType: fieldTypes.simpleField,
    columnNames: ['patientid', 'patient id', 'patient serial number', 'patient central id'],
    required: false,
    transform: val => trimOrUndefined(val?.toString()),
  },
  mrn: {
    fieldType: fieldTypes.simpleField,
    columnNames: ['mrn', 'medical record number'],
    required: false,
    transform: val => trimOrUndefined(val),
  },
  dateOfBirth: {
    fieldType: fieldTypes.simpleField,
    columnNames: ['dob', 'date of birth', 'patient dob', 'patient date of birth'],
    required: true,
    transform: val => readDate(val),
  },
  textPhoneNo: {
    fieldType: fieldTypes.simpleField,
    columnNames: ['textnumber', 'alt. phone number(s)', 'mobile phone', 'patient mobile number', 'patient primary phone number', 'phonenumber', 'primary phone number', 'mobile phone', 'patient mobile number'],
    required: false,
    transform: val => calcPhoneNumber(val),
  },
  email: {
    fieldType: fieldTypes.simpleField,
    columnNames: ['email', 'personal email', 'patient email'],
    required: false,
    transform: val => trimOrUndefined(val),
  },
  homeAddress: {
    fieldType: fieldTypes.simpleField,
    columnNames: ['address', 'patient address'],
    required: false,
    transform: val => trimOrUndefined(val),
  },
  address: {
    fieldType: fieldTypes.combinedField,
    columnNames: addressFields,
    required: false,
    transform: (fields, val) => getAddressFromFields(fields, val),
  },
  facility: {
    fieldType: fieldTypes.simpleField,
    columnNames: ['facility', 'facility name'],
    required: false,
    transform: val => trimOrUndefined(val),
  },

  insurance: {
    fieldType: fieldTypes.complexArray,
    columnNames: [
      ['insurancename', 'insurance name', 'ins name', 'third party', 'insurance'],
      ['policyid', 'policy id', 'card holder id', 'cardholderid', 'customer insurance id'],
      ['groupnumber', 'group #', 'group number'],
    ],
    transform: (n, id, gn) => {
      return {
        insuranceName: n,
        policyId: id,
        groupNumber: gn,
      };
    },
  },

  centerDetails: {
    fieldType: fieldTypes.complexArray,
    columnNames: [
      ['centername', 'center/practice name'],
      ['orderingprovider', 'ordering provider'],
      ['cliniccontact', 'name of person completing form'],
    ],
    transform: (n, p, cc) => {
      return {
        centerName: n,
        orderingProvider: p,
        clinicContact: cc,
      };
    },
  },

  conditions: {
    fieldType: fieldTypes.simpleArray,
    columnNames: ['condition', 'icd10 code'],
    transform: c => {
      return conditions.find(cnd => cnd.title === c || cnd.condition_icd10cm === c)?.id || c;
    },
  },

  transplantDetails: {
    fieldType: fieldTypes.complexArray,
    columnNames: [
      ['transplantorgantype', 'transplant organ'],
      ['transplantdate', 'transplant date'],
    ],
    transform: (ot, d) => {
      return {
        organType: ot,
        transplantDate: readDate(d),
      };
    },
  },

  timezone: { columnNames: ['timezone'], transform: val => trimOrUndefined(val) },
  gender: { columnNames: ['gender', 'sex', 'patient gender'], required: true, transform: val => getGender(val) },
  language: {
    columnNames: ['language', 'patient language'],
    required: false,
    transform: val => {
      const v = trimOrUndefined(val);
      const lang = languageValueToId(v);
      return lang || defaultLanguage;
    },
  },
  medications: {
    fieldType: fieldTypes.complexArray,
    columnNames: [
      ['medication', 'medication name', 'drug name', 'dispensed item name'],
      ['ndc', 'ndc code', 'dispensed item ndc'],
      ['rxnumber', 'rx number'],
      ['prescribed medication', 'prescribed item'],
      ['dispensed medication', 'dispensed item', 'dispensed item print name'],
      ['filldate', 'fill date', 'date filled'],
      ['prescriberfirstname', 'prescriber first name'],
      ['prescriberlastname', 'prescriber last name', 'prescriber full name', 'prescriberfullname'],
      ['prescriberprimaryphone', 'prescriber primary phone'],
      ['prescribertype', 'prescriber type'],
      ['prescribernpi', 'prescriber npi'],
      ['prescriberfaxnumber', 'prescriber fax number'],
      ['prescriberemail', 'prescriber email'],
    ],
    required: false,
    transform: (m, ndc, rx, pm, dm, fd, fn, ln, pp, pt, npi, fnn, e) => {
      return {
        name: m,
        ndc: ndc?.toString(),
        rxNumber: rx?.toString(),
        prescribedName: pm,
        dispensedName: dm,
        fillDate: readDate(fd),
        prescriber : {
          firstName: fn,
          lastName: ln?.includes(`, ${fn}`) ? ln.split(`, ${fn}`)[0] : ln,
          primaryPhone: pp?.toString(),
          type: pt,
          NPI: npi?.toString(),
          fax: fnn?.toString(),
          email: e,
        }
      };
    }
  },
});

const sufixes = ['primary', 'secondary', 'tertiary', 'quaternary', 'quinary', 'senary', 'septenary', 'octonary', 'nonary', 'denary'];
export const sufixesRegex = new RegExp (`^(${sufixes.join('|')})`, 'g');

const resolvePatientFieldKey = (patientFieldLookup, options) => {
  for (let i = 0; i < options.length; i += 1) {
    const key = patientFieldLookup.get(options[i]);
    if (key) {
      return key;
    }
  }
  return undefined;
};

export const prepareForComplexArray = (patient, patientFieldLookup, fields, packer) => {
  const ret = [];
  const maxCount = 10;
  for (let i = 1; i <= maxCount; i += 1) {
    const fieldValues = [];
    fields.forEach(fs => {
      let fv;
      fs.forEach(f => {
        const key = resolvePatientFieldKey(
          patientFieldLookup,
          i === 1 ? [f, `${f} ${i}`, `${f}${i}`] : [`${f} ${i}`, `${f}${i}`],
        );
        if (key) {
          fv = patient[key];
        }
      });

      const fvFinal = trimOrUndefined(fv);
      fieldValues.push(fvFinal);
    });

    if (fieldValues.filter(v => v).length > 0) {
      ret.push(packer(...fieldValues));
    }
  }
  sufixes.forEach(sufix => {
    const fieldValues = [];
    fields.forEach(fs => {
      let fv;
      fs.forEach(f => {
        const key = resolvePatientFieldKey(patientFieldLookup, [`${sufix} ${f}`, `${sufix}${f}`]);
        if (key) {
          fv = patient[key];
        }
      });

      const fvFinal = trimOrUndefined(fv);
      fieldValues.push(fvFinal);
    });

    if (fieldValues.filter(v => v).length > 0) {
      ret.push(packer(...fieldValues));
    }
  });
  return ret;
};

export const prepareForSimpleArray = (patient, patientFieldLookup, fields, packer) => {
  const ret = [];
  const maxCount = 10;
  for (let i = 1; i <= maxCount; i += 1) {
    fields.forEach(f => {
      let fv;
      const key = resolvePatientFieldKey(
        patientFieldLookup,
        i === 1 ? [f, `${f} ${i}`, `${f}${i}`] : [`${f} ${i}`, `${f}${i}`],
      );
      if (key) {
        fv = patient[key];
      }
      const fvFinal = trimOrUndefined(fv);
      if (fvFinal) {
        ret.push(packer(fvFinal));
      }
    });
  }

  sufixes.forEach(sufix => {
    fields.forEach(f => {
      let fv;
      const key = resolvePatientFieldKey(patientFieldLookup, [`${sufix} ${f}`, `${sufix}${f}`]);
      if (key) {
        fv = patient[key];
      }
      const fvFinal = trimOrUndefined(fv);
      if (fvFinal) {
        ret.push(packer(fvFinal));
      }
    });
  });
  return ret;
};

export const prepareForCombinedValue = (patient, patientFieldLookup, fields, packer) => {
  const args = fields.map(f => {
    const key = patientFieldLookup.get(f);
    if (key) {
      const trimmedValue = trimOrUndefined(patient[key]);
      return {
        field: f,
        value: trimmedValue || undefined,
      };
    }
  });
  return packer(fields, args);
};
